'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface FaqItem {
  question: string;
  answer: string;
  category: string;
}

const FAQS: FaqItem[] = [
  {
    category: 'Omnichannel & Instagram',
    question: 'How does Instagram DM automation work in Zeric?',
    answer:
      'Zeric connects natively to your Instagram Professional/Creator account via Meta Graph API v21.0. When anyone sends a direct message to your Instagram handle, our webhook ingests the message in real time, triggers your Zeric AI agent or automation workflows, and syncs the conversation to your unified team CRM inbox. Human agents can reply directly from the same window.',
  },
  {
    category: 'AI & Training',
    question: 'Can I train Zeric AI on our company documents, PDFs, and website FAQs?',
    answer:
      'Yes! You can upload company policy documents, price sheets, product catalogs, and website links directly into the Zeric AI Knowledge Base. The AI uses semantic vector search to retrieve accurate facts before answering, ensuring zero hallucinations and strict adherence to your brand guidelines.',
  },
  {
    category: 'Human Takeover',
    question: 'Can my human team take over a conversation from the AI at any time?',
    answer:
      'Absolutely. With one click, an agent can claim a chat, which automatically pauses AI responses for that conversation. The AI transitions into "Co-Pilot" mode, drafting suggested responses in the background while human agents talk directly to VIP customers. Chats can also be auto-assigned using department round-robin logic.',
  },
  {
    category: 'WhatsApp APIs',
    question: 'Do you support official WhatsApp Business API (WABA) or WhatsApp Web?',
    answer:
      'Zeric supports both! You can connect via Meta WhatsApp Cloud API (official green-badge capable, high throughput) or via Evolution API (WhatsApp Web QR code). You can even run both simultaneously across multiple phone numbers and instances.',
  },
  {
    category: 'E-Commerce & Orders',
    question: 'How do customers buy products directly inside the chat?',
    answer:
      'Using our E-Commerce and Catalog features, you can create interactive product menus with photos, specs, and price tags. Agents and AI can send these cards directly in WhatsApp, Instagram, or Messenger with a 1-click checkout link powered by Stripe, Razorpay, or custom payment gateways.',
  },
  {
    category: 'Setup & Onboarding',
    question: 'How long does it take to get set up?',
    answer:
      'You can be up and running in under 5 minutes. Simply register your account, scan your WhatsApp QR code or paste your Meta App token for Instagram/Facebook, upload your prompt instructions or FAQs, and your autonomous AI agent will immediately begin answering inquiries.',
  },
];

export function InteractiveFaq() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);

  return (
    <div className="relative mx-auto max-w-4xl w-full text-left">
      <div className="text-center mb-12">
        <Badge
          variant="secondary"
          className="mb-3 px-3.5 py-1 rounded-full text-xs font-semibold border-primary/20 bg-primary/5 text-primary gap-1.5"
        >
          <HelpCircle className="size-3.5" />
          Frequently Asked Questions
        </Badge>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
          Everything You Need to Know
        </h2>
        <p className="text-muted-foreground text-base md:text-lg">
          Clear answers about connecting Instagram, WhatsApp APIs, AI training, and team pricing.
        </p>
      </div>

      <div className="space-y-3.5">
        {FAQS.map((faq, idx) => {
          const isOpen = openIdx === idx;

          return (
            <div
              key={idx}
              className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
                isOpen
                  ? 'border-primary/40 bg-card shadow-md ring-1 ring-primary/20'
                  : 'border-border/70 bg-card/60 hover:border-border hover:bg-card'
              }`}
            >
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? null : idx)}
                className="flex items-center justify-between w-full p-5 text-left text-sm sm:text-base font-bold text-foreground transition-colors"
              >
                <span className="flex items-center gap-3">
                  <span className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-mono shrink-0">
                    0{idx + 1}
                  </span>
                  <span>{faq.question}</span>
                </span>
                <span className="size-8 rounded-xl bg-muted/60 flex items-center justify-center text-muted-foreground shrink-0 ml-3">
                  {isOpen ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                </span>
              </button>

              {isOpen && (
                <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed border-t border-border/40 mt-1">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
